// /app/frontend/src/components/innerwork/ShareReflectionModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { sendMessage } from "../../api/messages";
import { shareReflection } from "../../api/innerWork";

function defaultSnippet(text, maxLen = 240) {
  const t = (text || "").trim();
  if (t.length <= maxLen) return t;
  return t.slice(0, maxLen).trimEnd() + "…";
}

export default function ShareReflectionModal({
  open,
  onClose,
  reflection,
  matches,
  onShared,
}) {
  const [matchId, setMatchId] = useState("");
  const [snippet, setSnippet] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr("");
    setSending(false);
    setMatchId("");
    setSnippet(reflection?.content || reflection?.reflection_text || "");
  }, [open, reflection]);

  const canShare = useMemo(() => {
    return !!matchId && snippet.trim().length > 0 && !sending;
  }, [matchId, snippet, sending]);

  const doShare = async () => {
    if (!canShare) return;
    setErr("");
    setSending(true);
    try {
      // Find match name for confirmation
      const match = (matches || []).find(m => m.match_id === matchId);
      const matchLabel = match?.profile?.display_name || 
        (Array.isArray(match?.profile?.names) ? match.profile.names.join(" & ") : null) ||
        "your match";

      // 1) Send chat message (SSE will deliver it back into chat UI)
      const messageText = `Inner Work (shared):\n${snippet.trim()}`;
      const msgRes = await sendMessage({ match_id: matchId, content: messageText });

      // 2) Record share (audit trail + future analytics)
      const maybeMessageId =
        msgRes?.id || msgRes?.message_id || msgRes?._id || null;

      await shareReflection(reflection.id, {
        share_type: "chat_message",
        match_id: matchId,
        message_id: maybeMessageId,
        snippet_text: snippet.trim(),
      });

      onShared?.();
      onClose?.();
      
      // Brief success confirmation
      alert(`✓ Shared to ${matchLabel}`);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "Could not share reflection.";
      setErr(msg);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-xl rounded-2xl surface-card p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">Share to chat</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Your full reflection is shared as written. You can edit it before sending.
            </div>
          </div>
          <button
            type="button"
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-gray-200 transition"
            onClick={onClose}
            disabled={sending}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-4">
          <div className="text-xs font-medium text-gray-700">Choose a chat</div>
          <select
            className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-gray-300 transition"
            value={matchId}
            onChange={(e) => setMatchId(e.target.value)}
            disabled={sending}
          >
            <option value="">— Select a match —</option>
            {(matches || []).map((m) => {
              const names = Array.isArray(m?.profile?.names) ? m.profile.names : [];
              const label =
                (names.length ? names.join(" & ") : null) ||
                m?.profile?.display_name ||
                `Match ${m?.match_id || ""}`;
              return (
                <option key={m.match_id} value={m.match_id}>
                  {label}
                </option>
              );
            })}
          </select>
          {(!matches || matches.length === 0) && (
            <div className="mt-1 text-[11px] text-amber-600">
              No matches found. You need an active match to share reflections.
            </div>
          )}
        </div>

        <div className="mt-4">
          <div className="text-xs font-medium text-gray-700">Message snippet (editable)</div>
          <textarea
            className="mt-1 h-32 w-full resize-none rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-gray-300 transition"
            value={snippet}
            onChange={(e) => setSnippet(e.target.value)}
            disabled={sending}
            placeholder="Edit the snippet you want to share..."
          />
          <div className="mt-1 text-[11px] text-gray-500">
            This will send as: &quot;Inner Work (shared): …&quot;
          </div>
        </div>

        {err ? <div className="mt-3 text-sm text-red-600">{err}</div> : null}

        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
            onClick={onClose}
            disabled={sending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-xl bg-rose-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-rose-600 disabled:opacity-50 transition"
            onClick={doShare}
            disabled={!canShare}
          >
            {sending ? "Sharing…" : "Share to chat"}
          </button>
        </div>
      </div>
    </div>
  );
}
